import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";
import { IMG_URl } from "../../common/img_url";
import { indexStore } from "../../store/indexStore";
import Visibility from "../utils/Visibility";
import OptionsProduct from "../OptionsProduct/OptionsProduct";
import {
  ProductCartElement,
  ProductDetails,
  ProductTags,
} from "./ProductElemnts";
import { Skeleton } from "../ui/skeleton";
import Iterator from "../utils/Iterator";
import ProductModel from "../../Models/PorductModel";
import { CategoryModel } from "../../Models/CategoryModel";

// ============================================================================
// Constants
// ============================================================================

const MIN_IMAGE_HEIGHT = 200;
const INITIAL_IMAGE_HEIGHT = 300;
const SCROLL_SHRINK_FACTOR = 0.5;

// ============================================================================
// NewProductScreen Component
// ============================================================================

export default function NewProductScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { name, description, product, itemLoading, lang } = indexStore();
  const params = useParams();
  const [scrollY, setScrollY] = useState(0);
  const [optionsSelected, setOptionsSelected] = useState<any[]>([]);

  const handleBack = () => {
    navigate(-1);
  };

  // Handle scroll for dynamic image height
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const imageHeight = Math.max(
    MIN_IMAGE_HEIGHT,
    INITIAL_IMAGE_HEIGHT - scrollY * SCROLL_SHRINK_FACTOR
  );

  if (itemLoading) {
    return (
      <div className="min-h-screen">
        <RenderProduct />
        <ProductCartElement
          productSheet={false}
          product={product}
          t={t}
          navigate={navigate}
          params={params}
          optionsSelected={optionsSelected}
          itemLoading={itemLoading}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16 md:pb-24">
      {/* Product Image */}
      <div
        className="w-full overflow-hidden transition-all duration-300 opacity-0 animate-fadeIn"
        style={{ height: `${imageHeight}px` }}
      >
        <img
          src={`${IMG_URl}${product.image}`}
          alt={product[name as keyof ProductModel] as string}
          className="w-full h-full object-cover rounded-b-3xl shadow-lg"
        />
      </div>

      {/* Product Content */}
      <div className="p-5 md:p-6 space-y-4 md:space-y-5">
        <div className="flex items-center justify-between gap-4 opacity-0 animate-[fadeIn_0.3s_ease-out_0.2s_forwards]">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="p-2 rounded-lg hover:bg-muted/50 transition-all duration-300 active:scale-95 text-foreground flex-shrink-0"
          >
            {lang === "ar" ? (
              <FaAngleRight className="w-5 h-5 md:w-6 md:h-6" />
            ) : (
              <FaAngleLeft className="w-5 h-5 md:w-6 md:h-6" />
            )}
          </button>

          {/* Product Title and Category - Centered */}
          <div className="flex flex-col gap-2 flex-1 items-center">
            <h1 className="text-2xl text-primary md:text-3xl font-bold text-center">
              {product?.[name as keyof ProductModel]?.toString()}
            </h1>
            <h3 className="text-lg md:text-2xl font-bold text-center">
              {product.category?.[name as keyof CategoryModel]?.toString()}
            </h3>
          </div>

          {/* Spacer for symmetry */}
          <div className="w-9 md:w-10 flex-shrink-0" />
        </div>

        {/* Product Tags */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.3s_forwards]">
          <ProductTags product={product} t={t} />
        </div>

        {/* Product Details */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.4s_forwards]">
          <ProductDetails product={product} name={name} description={description} />
        </div>

        {/* Options */}
        <Visibility visible={product.options && product.options.length > 0}>
          <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.5s_forwards]">
            <OptionsProduct
              options={product.options}
              name={name}
              price={product.price_offer || product.price}
              setOptionsSelected={setOptionsSelected}
              optionsSelected={optionsSelected}
            />
          </div>
        </Visibility>

        {/* Cart Element */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.6s_forwards]">
          <ProductCartElement
            productSheet={false}
            product={product}
            t={t}
            navigate={navigate}
            params={params}
            optionsSelected={optionsSelected}
            itemLoading={itemLoading}
          />
        </div>
      </div>
    </div>
  );
}
// ============================================================================
// Loading Component
// ============================================================================

const RenderProduct = () => {
  return (
    <div className="min-h-screen">
      {/* Image Skeleton - matches actual image height */}
      <Skeleton className="w-full h-80 rounded-b-3xl" />

      {/* Content Skeleton */}
      <div className="p-5 md:p-6 space-y-4 md:space-y-5">
        {/* Header Skeleton - Back Button + Title/Category + Spacer */}
        <div className="flex items-center justify-between gap-4 opacity-0 animate-[fadeIn_0.3s_ease-out_0.2s_forwards]">
          {/* Back Button Skeleton */}
          <Skeleton className="w-9 h-9 md:w-10 md:h-10 rounded-lg flex-shrink-0" />

          {/* Title and Category Skeleton - Centered */}
          <div className="flex flex-col gap-2 flex-1 items-center">
            <Skeleton className="w-3/4 md:w-1/2 h-7 md:h-8" />
            <Skeleton className="w-1/2 md:w-1/3 h-6 md:h-7" />
          </div>

          {/* Spacer for symmetry */}
          <div className="w-9 md:w-10 flex-shrink-0" />
        </div>

        {/* Tags Skeleton */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.3s_forwards]">
          <div className="flex gap-1 flex-wrap">
            <Skeleton className="w-16 h-7 rounded-md" />
            <Skeleton className="w-20 h-7 rounded-md" />
          </div>
        </div>

        {/* Product Details Skeleton */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.4s_forwards]">
          <div className="space-y-3">
            {/* Description Skeleton */}
            <div className="space-y-2">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-5/6 h-4" />
              <Skeleton className="w-4/6 h-4" />
            </div>

            {/* Price Skeleton - matches actual layout with gap-[65%] */}
            <div className="text-base font-semibold flex justify-end gap-[65%]">
              <Skeleton className="w-20 h-6" />
              <Skeleton className="w-24 h-6" />
            </div>
          </div>
        </div>

        {/* Options Skeleton */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.5s_forwards]">
          <div className="flex flex-col gap-3">
            <Skeleton className="w-24 h-5" />
            <div className="flex flex-col gap-2">
              <div className="px-1 flex flex-col gap-2">
                <Skeleton className="w-20 h-5" />
                <div className="px-2 flex flex-col gap-2">
                  <Iterator
                    data={[1, 2, 3]}
                    render={() => (
                      <div className="flex items-center gap-3">
                        <Skeleton className="w-4 h-4 rounded-full" />
                        <Skeleton className="w-40 h-5" />
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cart Element Skeleton */}
        <div className="opacity-0 animate-[fadeIn_0.3s_ease-out_0.6s_forwards]">
          <div className="flex flex-col gap-2">
            {/* Quantity Selector Skeleton */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Skeleton className="w-20 h-6" />
                <div className="flex items-center gap-4">
                  <Skeleton className="w-10 h-10 rounded-lg" />
                  <Skeleton className="w-8 h-6" />
                  <Skeleton className="w-10 h-10 rounded-lg" />
                </div>
              </div>
            </div>

            {/* Note Textarea Skeleton */}
            <Skeleton className="w-full h-20 rounded-md" />

            {/* Add to Cart Button Skeleton */}
            <Skeleton className="w-full h-12 rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
};
